import { z } from "zod";

import { DatabaseIdentity } from "@/core/api";
import { DateIsoString, Pageable } from "@/core/types";
import { WorkStyleType } from "@/data/consultant-profiles/types/WorkStyleType";
import { BrokerFee } from "@/data/job-requests/types/BrokerFee";
import { JobRequestClient } from "@/data/job-requests/types/JobRequestClient";
import { JobRequestOrigin } from "@/data/job-requests/types/JobRequestOrigin";
import { JobRequestPayExpressConfig } from "@/data/job-requests/types/JobRequestPayExpressConfig";
import { JobRequestStatus } from "@/data/job-requests/types/JobRequestStatus";
import { Location } from "@/data/locations/types/Location";
import { Question } from "@/data/questionnaire/types/Question";
import { LegalEntityShort } from "@/data/types/LegalEntityShort";
import { UserBase } from "@/data/users";

import { JobRequestRate } from "./JobRequestRate";

export const JobRequest = z
  .object({
    systemId: z.string(),
    title: z.string().nullish(),

    status: JobRequestStatus,
    origin: JobRequestOrigin,
    rate: JobRequestRate,

    client: JobRequestClient.nullish(),
    legalEntityClient: LegalEntityShort.nullish(),
    administratorLegalEntityClient: LegalEntityShort.nullish(),

    hiringManager: UserBase.nullish(),

    promoteClient: z.boolean().nullish(),

    startDate: DateIsoString.nullish(),
    endDate: DateIsoString.nullish(),

    locations: z.array(Location).nullish(),
    questions: z.array(Question).nullish(),
    workStyles: z.array(WorkStyleType).nullish(),

    brokerFee: BrokerFee.nullish(),
    paymentTerm: z.number().nullish(),
    supplierPaymentTerm: z.number().nullish(),
    payExpressConfig: JobRequestPayExpressConfig.nullish(),
  })
  .merge(DatabaseIdentity)
  .passthrough();

export type JobRequest = z.infer<typeof JobRequest>;

export const JobRequestListDto = z
  .object({
    id: z.number(),
    title: z.string().optional(),
    systemId: z.string(),
    firstDayOfApplications: z.string().nullish(),
    legalEntityClient: LegalEntityShort.nullish(),
    administratorLegalEntityClient: LegalEntityShort.nullish(),
    client: z
      .object({
        name: z.string().nullable(),
        logo: z
          .object({
            publicUrl: z.string().nullable(),
          })
          .nullable(),
      })
      .optional(),
    origin: z.object({
      name: z.string().optional(),
      details: z
        .object({
          logo: z.string().nullable(),
          vatNumber: z.string().optional(),
        })
        .optional(),
    }),
    rate: z.object({
      currency: z.string(),
      maxRate: z.number().nullable(),
      clientRateType: z.string().nullable(),
    }),
    locations: z
      .array(
        z.object({
          city: z.string().nullable(),
          countryCode: z.string().nullish(),
          name: z.string().optional(),
        }),
      )
      .nullish(),
    public: z.boolean(),
    remoteness: z.number().nullish(),
    promoteClient: z.boolean().optional(),
    favourite: z.boolean().optional(),
  })
  .passthrough();
export type JobRequestListDto = z.infer<typeof JobRequestListDto>;

export const JobRequestListDtoPageable = Pageable(JobRequestListDto);
export type JobRequestListDtoPageable = z.infer<typeof JobRequestListDtoPageable>;

export const JobRequestV2 = z.object({
  id: z.number(),
  title: z.string(),
  systemId: z.string(),
  firstDayOfApplications: z.string().nullish(),
  name: z.string().nullable(),
  originServiceName: z.string(),
  city: z.string().nullable(),
  countryCode: z.string().nullable(),
  publicUrl: z.string().nullable(), // maps to client.logo.publicUrl
  logo: z.string().nullable(), // maps to origin.details.logo
  remoteness: z.number(),
  currency: z.string(),
  maxRate: z.number().nullable(),
  clientRateType: z.enum(["HOURLY", "FIXED"]),
  public: z.boolean(),
  clientLegalEntityId: z.number().nullable(),
  clientLegalEntityName: z.string().nullable(),
  clientLegalEntityLogo: z.string().nullable(),
  administratorLegalEntityId: z.number().nullable(),
  administratorLegalEntityName: z.string().nullable(),
  administratorLegalEntityLogo: z.string().nullable(),
  promoteClient: z.boolean(),
  favourite: z.boolean(),
});
export type JobRequestV2 = z.infer<typeof JobRequestV2>;

export const JobRequestV2Pageable = Pageable(JobRequestV2);
export type JobRequestV2Pageable = z.infer<typeof JobRequestV2Pageable>;
